import { useEffect, useId, useState } from 'react';
import { api } from '../../lib/api';
import { SelectInput, NumberInput } from '../../components/ui';

// 统一工序编辑器（售后返修单件 + 厂内返工按件共用）：
//  · 机加工：粗磨 / 精磨 / 倒角 / 抛光 四个固定多选项，每项填工时 → 批量创建多条 rework_operations；
//  · 激光熔覆：单选「激光熔覆」+ 粉末材料（GET /api/rework-batches/powders）+ 耗料数量/单位 + 工时
//    → 创建一条 rework_operation + 登记一条 powder usage（即时扣库 / 关联工单领料核销）。
// 已安排工序列表的编辑/删除仍由父面板负责（接口不变）。
// 排版（task-33）：机加工改为稳定网格「勾选框 + 工序名 + 工时 + h」，窄屏自动单列，避免 checkbox
// 与工时输入错位；激光熔覆区改为同高表单网格并对齐标签，不再用固定宽度横排挤压。

export const MACHINING_OPS = [
  { code: 'ROUGH_GRIND', name: '粗磨' },
  { code: 'FINE_GRIND', name: '精磨' },
  { code: 'CHAMFER', name: '倒角' },
  { code: 'POLISH', name: '抛光' },
] as const;

const LASER_CLAD = { code: 'LASER_CLAD', name: '激光熔覆' } as const;

type PowderOption = { id: string; code: string; name: string; uom?: string | null };
type PowderCandidate = {
  id: string;
  work_order_number?: string | null;
  item_name_zh?: string | null;
  item_code?: string | null;
  remaining_base?: number | null;
};
type RawPowder = {
  id?: string; item_id?: string; code?: string; name_zh?: string; item_code?: string;
  item_name_zh?: string; stock_uom?: string | null; inventory_policy?: string | null;
};
type RawItem = {
  id: string;
  code: string;
  name_zh: string;
  name_es?: string | null;
  item_group?: string | null;
  stock_uom?: string | null;
  inventory_policy?: string | null;
};

// 与后端 listPowders（task-18 阻塞修复）同口径的回退过滤：
//   ① inventory_policy='powder'；② code 前缀 302001/302099；③ code/name_zh/name_es/item_group 含
//   粉|合金|powder|polvo；任一命中即视为粉末（生产/脱敏库物料常见 inventory_policy='normal'）。
const isPowderCode = (code?: string | null) => /^3020(01|99)/.test(String(code || '').trim());
function isPowderMaterial(it: RawItem): boolean {
  if (it.inventory_policy === 'powder') return true;
  if (isPowderCode(it.code)) return true;
  const text = `${it.code || ''} ${it.name_zh || ''} ${it.name_es || ''} ${it.item_group || ''}`;
  return /粉|合金|powder|polvo/i.test(text);
}

const numFilled = (v: string) => v.trim() !== '' && Number.isFinite(Number(v)) && Number(v) >= 0;
const EMPTY_POWDER_HINT = '未取到可用粉末物料，请检查物料主数据';

export function OperationEditor({ endpoint, canWrite, onChanged }: {
  /** 工序主体接口前缀：批次单件 `/api/rework-batches/items/:id`、厂内返工整单 `/api/production-rework/tasks/:id`
   *  或按件 `/api/production-rework/tasks/:id/items/:itemId`（task-32 起）。 */
  endpoint: string;
  canWrite: boolean;
  onChanged: () => void;
}) {
  const uid = useId();
  const [mode, setMode] = useState<'machining' | 'laser'>('machining');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  // 机加工：选中项 + 各自工时
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [hours, setHours] = useState<Record<string, string>>({});

  // 激光熔覆
  const [laser, setLaser] = useState({
    hours: '', powderId: '', powderLabel: '', quantity: '', unit: 'kg',
    issueMode: 'immediate' as 'immediate' | 'work_order_link', snapshotId: '',
  });
  const [powders, setPowders] = useState<PowderOption[]>([]);
  const [powderErr, setPowderErr] = useState('');
  const [candidates, setCandidates] = useState<PowderCandidate[]>([]);

  // 粉末材料候选：优先后端专用接口；接口异常/为空时回退物料主数据（过滤口径与后端一致）。
  useEffect(() => {
    let alive = true;
    const fallback = () => api.list<RawItem>('/api/items', { limit: 200 })
      .then((list) => list
        .filter(isPowderMaterial)
        .map((it) => ({ id: it.id, code: it.code, name: it.name_zh, uom: it.stock_uom || null })))
      .catch(() => [] as PowderOption[]);
    // 最终为空时必须显式提示，不能静默留空下拉。
    const applyFallback = () => fallback().then((items) => {
      if (!alive) return;
      setPowders(items);
      if (!items.length) setPowderErr(EMPTY_POWDER_HINT);
    });
    api.list<RawPowder>('/api/rework-batches/powders')
      .then((list) => {
        if (!alive) return;
        const mapped = (list || []).map((p) => ({
          id: String(p.id || p.item_id || ''),
          code: String(p.code || p.item_code || ''),
          name: String(p.name_zh || p.item_name_zh || ''),
          uom: p.stock_uom || null,
        })).filter((p) => p.id);
        if (mapped.length) { setPowders(mapped); setPowderErr(''); return; }
        setPowderErr(EMPTY_POWDER_HINT);
        void applyFallback();
      })
      .catch(() => {
        if (!alive) return;
        // 后端粉末接口未就绪时回退到物料主数据（inventory_policy=powder / 编码前缀 / 名称关键字），并提示。
        setPowderErr('粉末专用接口不可用，已回退到物料主数据；如列表不全请稍后重试。');
        void fallback().then((items) => {
          if (!alive) return;
          setPowders(items);
          if (!items.length) setPowderErr(`${EMPTY_POWDER_HINT}（粉末专用接口不可用）`);
        });
      });
    return () => { alive = false; };
  }, []);

  // 关联工单领料核销：按所选粉末找可核销的工单领料快照。
  useEffect(() => {
    setCandidates([]);
    if (laser.issueMode !== 'work_order_link' || !laser.powderId) return;
    let alive = true;
    api.list<PowderCandidate>('/api/rework-batches/powder-link-candidates', { item_id: laser.powderId })
      .then((rows) => { if (alive) setCandidates(rows); })
      .catch(() => { if (alive) setCandidates([]); });
    return () => { alive = false; };
  }, [laser.issueMode, laser.powderId]);

  const resetMachining = () => { setPicked({}); setHours({}); };
  const resetLaser = () => setLaser({ hours: '', powderId: '', powderLabel: '', quantity: '', unit: 'kg', issueMode: 'immediate', snapshotId: '' });

  const createOp = (code: string, name: string, plannedHours: number) =>
    api.post<{ data?: { id?: string } }>(`${endpoint}/operations`, {
      operation_code: code,
      operation_name: name,
      planned_hours: Number(plannedHours),
    });

  const submitMachining = async () => {
    setErr(''); setMsg('');
    const chosen = MACHINING_OPS.filter((o) => picked[o.code]);
    if (!chosen.length) { setErr('请至少选择一项机加工工序'); return; }
    const invalid = chosen.find((o) => !numFilled(hours[o.code] || ''));
    if (invalid) { setErr(`请填写「${invalid.name}」的工时（小时）`); return; }
    setBusy(true);
    const fails: string[] = [];
    let ok = 0;
    for (const o of chosen) {
      try {
        await createOp(o.code, o.name, Number(hours[o.code]));
        ok += 1;
      } catch (e: any) {
        fails.push(`${o.name}：${e?.message || '添加失败'}`);
      }
    }
    setBusy(false);
    if (fails.length) {
      setErr(`已添加 ${ok} 项，${fails.length} 项失败：${fails.join('；')}`);
    } else {
      setMsg(`已添加 ${ok} 项机加工工序`);
      resetMachining();
      onChanged();
    }
  };

  const submitLaser = async () => {
    setErr(''); setMsg('');
    if (!numFilled(laser.hours)) { setErr('请填写激光熔覆工时（小时）'); return; }
    if (!laser.powderId) { setErr('请选择粉末材料'); return; }
    if (!laser.quantity.trim() || !(Number(laser.quantity) > 0)) { setErr('请填写耗料数量（大于 0）'); return; }
    if (laser.issueMode === 'work_order_link' && !laser.snapshotId) { setErr('关联工单领料核销需选择工单领料快照'); return; }
    setBusy(true);
    let opId = '';
    try {
      const out: any = await createOp(LASER_CLAD.code, LASER_CLAD.name, Number(laser.hours));
      const created = out?.data ?? out;
      opId = String(created?.id || '');
      if (!opId) throw new Error('工序创建成功但未返回工序 ID');
    } catch (e: any) {
      setErr(e?.message || '激光熔覆工序添加失败');
      setBusy(false);
      return;
    }
    // 工序已建：粉末登记失败时给出可操作的补登/调整提示，且刷新列表让工序可见。
    try {
      await api.post(`/api/rework-batches/operations/${encodeURIComponent(opId)}/powder`, {
        item_id: laser.powderId,
        actual_quantity: Number(laser.quantity),
        actual_unit: laser.unit || undefined,
        issue_mode: laser.issueMode,
        ...(laser.issueMode === 'work_order_link' ? { material_snapshot_id: laser.snapshotId } : {}),
      });
      setMsg(`激光熔覆工序已添加（${laser.hours}h），粉末耗用已登记（${laser.issueMode === 'immediate' ? '即时扣库' : '关联工单领料核销'}）。`);
      resetLaser();
    } catch (e: any) {
      setErr(`激光熔覆工序已创建，但粉末耗用登记失败：${e?.message || '未知错误'}。`
        + '请在工序行的「耗用」中补登，或核对库存后重试。');
    } finally {
      setBusy(false);
      onChanged();
    }
  };

  if (!canWrite) return null;

  return (
    <div className="panel" style={{ margin: '8px 0', padding: 12, background: 'var(--surface-2, #f7f7f7)' }}>
      <div className="strong" style={{ marginBottom: 6 }}>添加工序</div>
      <div className="detail-actions" style={{ flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        <button className={`btn ${mode === 'machining' ? 'primary' : ''}`} onClick={() => setMode('machining')}>机加工</button>
        <button className={`btn ${mode === 'laser' ? 'primary' : ''}`} onClick={() => setMode('laser')}>激光熔覆</button>
      </div>
      {err ? <div className="notice bad">{err}</div> : null}
      {msg ? <div className="notice">{msg}</div> : null}

      {mode === 'machining' ? (
        <div>
          <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
            勾选本件需要的机加工序（可多选），并在同一行右侧填写工时（小时）。
          </div>
          {/* 稳定网格：每个工序一张小卡（勾选框 + 名称 + 工时 + h），auto-fit 窄屏自动单列，不再错位。 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(215px, 1fr))', gap: 8 }}>
            {MACHINING_OPS.map((o) => (
              <div
                key={o.code}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, minWidth: 0,
                  padding: '6px 8px', border: '1px solid var(--line, #e2e5ea)', borderRadius: 8,
                  background: picked[o.code] ? 'var(--panel-sub, #f7f8fa)' : 'transparent',
                }}
              >
                <input
                  id={`${uid}-machining-${o.code}`}
                  type="checkbox"
                  checked={!!picked[o.code]}
                  onChange={(e) => setPicked((s) => ({ ...s, [o.code]: e.target.checked }))}
                  style={{ flex: '0 0 auto', margin: 0 }}
                />
                <label htmlFor={`${uid}-machining-${o.code}`} style={{ flex: '1 1 auto', minWidth: 44, cursor: 'pointer' }}>
                  {o.name}
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: '0 0 auto' }}>
                  <div style={{ width: 84, flex: '0 0 auto' }}>
                    <NumberInput
                      min={0}
                      step="0.1"
                      placeholder="工时"
                      disabled={!picked[o.code]}
                      value={hours[o.code] || ''}
                      onChange={(e) => setHours((s) => ({ ...s, [o.code]: e.target.value }))}
                      aria-label={`${o.name}工时（小时）`}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <span className="muted" style={{ fontSize: 12, width: 10 }}>h</span>
                </div>
              </div>
            ))}
          </div>
          <div className="detail-actions" style={{ marginTop: 8 }}>
            <button className="btn primary" disabled={busy} onClick={() => void submitMachining()}>
              {busy ? '添加中…' : '添加机加工序'}
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
            激光熔覆单选项：必须选择粉末材料并填写耗料数量与工时；按所选方式扣减公司粉末库存。
          </div>
          {/* 同高表单网格：每项带小标签，auto-fit 窄屏自动换行，避免固定宽度横排挤压错位。 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, alignItems: 'end' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 34, minWidth: 0 }}>
              <input type="checkbox" checked readOnly style={{ margin: 0 }} />
              激光熔覆
            </label>
            <div style={{ gridColumn: '1 / -1', minWidth: 0 }}>
              <div className="muted" style={{ fontSize: 11, marginBottom: 2 }}>粉末材料 *</div>
              <SelectInput
                value={laser.powderId}
                onChange={(e) => {
                  const id = e.target.value;
                  const p = powders.find((x) => x.id === id);
                  // 选中后默认单位取物料 stock_uom；无值/空串时回退 kg。
                  const stockUom = String(p?.uom || '').trim();
                  setLaser((s) => ({ ...s, powderId: id, powderLabel: p ? `${p.code} ${p.name}` : '', unit: stockUom || 'kg', snapshotId: '' }));
                }}
                aria-label="选择粉末材料"
                style={{ width: '100%' }}
              >
                <option value="">选择粉末材料…</option>
                {powders.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.name}</option>)}
              </SelectInput>
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="muted" style={{ fontSize: 11, marginBottom: 2 }}>耗料数量 *</div>
              <NumberInput
                min={0}
                step="0.01"
                placeholder="耗料数量"
                value={laser.quantity}
                onChange={(e) => setLaser((s) => ({ ...s, quantity: e.target.value }))}
                aria-label="耗料数量"
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="muted" style={{ fontSize: 11, marginBottom: 2 }}>单位</div>
              <SelectInput value={laser.unit} onChange={(e) => setLaser((s) => ({ ...s, unit: e.target.value }))} aria-label="耗料单位" style={{ width: '100%' }}>
                <option value="kg">kg</option>
                <option value="g">g</option>
                {!['kg', 'g'].includes(laser.unit) ? <option value={laser.unit}>{laser.unit}</option> : null}
              </SelectInput>
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="muted" style={{ fontSize: 11, marginBottom: 2 }}>工时（小时）*</div>
              <NumberInput
                min={0}
                step="0.1"
                placeholder="工时 h"
                value={laser.hours}
                onChange={(e) => setLaser((s) => ({ ...s, hours: e.target.value }))}
                aria-label="激光熔覆工时（小时）"
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="muted" style={{ fontSize: 11, marginBottom: 2 }}>扣库方式</div>
              <SelectInput
                value={laser.issueMode}
                onChange={(e) => setLaser((s) => ({ ...s, issueMode: e.target.value as 'immediate' | 'work_order_link', snapshotId: '' }))}
                aria-label="扣库方式"
                style={{ width: '100%' }}
              >
                <option value="immediate">即时扣库</option>
                <option value="work_order_link">关联工单领料核销</option>
              </SelectInput>
            </div>
            {laser.issueMode === 'work_order_link' ? (
              <div style={{ gridColumn: '1 / -1', minWidth: 0 }}>
                <div className="muted" style={{ fontSize: 11, marginBottom: 2 }}>工单领料快照</div>
                <SelectInput
                  value={laser.snapshotId}
                  onChange={(e) => setLaser((s) => ({ ...s, snapshotId: e.target.value }))}
                  aria-label="工单领料快照"
                  style={{ width: '100%' }}
                >
                  <option value="">选择工单领料（核销剩余）…</option>
                  {candidates.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.work_order_number || '工单'} · 剩余 {c.remaining_base ?? 0}
                    </option>
                  ))}
                </SelectInput>
              </div>
            ) : null}
          </div>
          <div className="detail-actions" style={{ marginTop: 8 }}>
            <button className="btn primary" disabled={busy} onClick={() => void submitLaser()}>
              {busy ? '添加中…' : '添加激光熔覆'}
            </button>
          </div>
          {powderErr ? <div className="notice bad" style={{ marginTop: 6 }}>{powderErr}</div> : null}
          {laser.issueMode === 'work_order_link' && laser.powderId && candidates.length === 0 ? (
            <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
              该粉末暂无可核销的工单领料快照；可改用「即时扣库」。
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
